import React, { useEffect, useState } from 'react';
import { Card, Col, Descriptions, Row, Spin, Tag, Typography, Button, Table, message, Grid } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';
import JournalVoucherModal from '../components/JournalVoucherModal';

const { Title } = Typography;

const PurchaseDetailsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { id } = useParams();
    const navigate = useNavigate();
    const [purchase, setPurchase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [voucherVisible, setVoucherVisible] = useState(false);

    useEffect(() => {
        fetchPurchaseDetails();
    }, [id]);

    const fetchPurchaseDetails = async () => {
        try {
            const { data } = await client.get(`/transactions/purchases/${id}`);
            setPurchase(data);
        } catch (error) {
            message.error('Failed to load purchase details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!purchase) {
        return (
            <div style={{ padding: 32, textAlign: 'center' }}>
                <Typography.Text type="secondary">Purchase record not found.</Typography.Text>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchasing')} style={{ marginTop: 16 }}>
                    Back to List
                </Button>
            </div>
        );
    }

    const fmt = (val, decimals = 2) => {
        const n = Number(val);
        return isNaN(n) ? '0.00' : n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const statusColor = purchase.status === 'POSTED' ? 'success' : 'processing';

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: isMobile ? 12 : 16, flexWrap: 'wrap' }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/purchasing')}
                        style={{ marginBottom: 12, borderRadius: 8 }}
                    >
                        Back to Purchases
                    </Button>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>
                        Receipt #{purchase.ticket_number}
                    </Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                        Detailed breakdown of procurement transaction and weight analysis.
                    </Typography.Text>
                </div>
                <Tag color={statusColor} style={{ padding: '4px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600 }}>
                    {purchase.status}
                </Tag>
                {purchase.status === 'POSTED' && (
                    <Button
                        type="primary"
                        ghost
                        icon={<PrinterOutlined />}
                        onClick={() => setVoucherVisible(true)}
                        style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                        Journal Voucher
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Gross Weight"
                        value={`${fmt(purchase.gross_weight)} kg`}
                        icon={<i className="fa-solid fa-scale-balanced"></i>}
                        color="#3b82f6"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Net Weight"
                        value={`${fmt(purchase.net_weight)} kg`}
                        icon={<i className="fa-solid fa-weight-scale"></i>}
                        color="#8b5cf6"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Payable Amount"
                        value={`PHP ${fmt(purchase.total_amount)}`}
                        icon={<i className="fa-solid fa-money-bill-wave"></i>}
                        color="#10b981"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Date Received"
                        value={dayjs(purchase.transaction_date).format('MMM DD, YYYY')}
                        icon={<i className="fa-solid fa-calendar-days"></i>}
                        color="#f59e0b"
                    />
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                    <Card className="premium-card" title={<Typography.Text strong>Inventory & Lots</Typography.Text>}>
                        <Table
                            dataSource={purchase.items || []}
                            pagination={false}
                            size={isMobile ? 'small' : 'middle'}
                            scroll={isMobile ? { x: 800 } : undefined}
                            columns={[
                                {
                                    title: 'Item Name',
                                    dataIndex: ['item', 'name'],
                                    key: 'item_name',
                                    render: (text) => <Typography.Text strong>{text}</Typography.Text>
                                },
                                {
                                    title: 'Lot Number',
                                    dataIndex: 'lot_number',
                                    key: 'lot_number',
                                    render: (text) => <Typography.Text strong style={{ color: '#3b82f6' }}>{text}</Typography.Text>
                                },
                                {
                                    title: 'Initial Weight',
                                    dataIndex: 'initial_quantity',
                                    key: 'initial_quantity',
                                    render: (qty) => <Typography.Text>{Number(qty).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg</Typography.Text>
                                },
                                {
                                    title: 'Available Stock',
                                    dataIndex: 'current_quantity',
                                    key: 'current_quantity',
                                    render: (qty) => <Typography.Text strong>{Number(qty).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg</Typography.Text>
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status) => <Tag color={status === 'ACTIVE' ? 'success' : 'default'}>{status}</Tag>
                                }
                            ]}
                        />

                        <div style={{ marginTop: 24 }}>
                            <Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>Weight Analysis</Typography.Text>
                            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
                                <Descriptions.Item label={<Typography.Text strong>Gross Weight</Typography.Text>}>{fmt(purchase.gross_weight)} kg</Descriptions.Item>
                                <Descriptions.Item label={<Typography.Text strong>Tare Weight</Typography.Text>}>{fmt(purchase.tare_weight)} kg</Descriptions.Item>
                                <Descriptions.Item label={<Typography.Text strong>Sack Deduction</Typography.Text>}>{fmt(purchase.sack_deduction)} kg</Descriptions.Item>
                                <Descriptions.Item label={<Typography.Text strong>Impurity Deduction</Typography.Text>}>{fmt(purchase.impurity_deduction)} kg</Descriptions.Item>
                                <Descriptions.Item label={<Typography.Text strong>Moisture Deduction</Typography.Text>}>{fmt(purchase.moisture_deduction)} kg</Descriptions.Item>
                                <Descriptions.Item label={<Typography.Text strong style={{ color: '#3b82f6' }}>Final Net Weight</Typography.Text>}>
                                    <Typography.Text strong style={{ color: '#3b82f6' }}>{fmt(purchase.net_weight)} kg</Typography.Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card className="premium-card" title={<Typography.Text strong>Partner & Pricing</Typography.Text>}>
                        <Descriptions column={1}>
                            <Descriptions.Item label={<Typography.Text strong>Supplier</Typography.Text>}>{purchase.partner?.name}</Descriptions.Item>
                            <Descriptions.Item label={<Typography.Text strong>Base Price</Typography.Text>}>PHP {fmt(purchase.base_price)} / kg</Descriptions.Item>
                            <Descriptions.Item label={<Typography.Text strong>Moisture Content</Typography.Text>}>{purchase.moisture_content ?? 0}%</Descriptions.Item>
                            <Descriptions.Item label={<Typography.Text strong>MC Adjustment</Typography.Text>}>
                                PHP {fmt(purchase.moisture_adjustment)}
                            </Descriptions.Item>
                            <Descriptions.Item label={<Typography.Text strong>Calculated Net Price</Typography.Text>}>
                                <Typography.Text strong>PHP {fmt(purchase.net_price)} / kg</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={<Typography.Text strong style={{ color: '#10b981' }}>Total Gross Value</Typography.Text>}>
                                <Title level={4} style={{ margin: 0, color: '#10b981' }}>
                                    PHP {fmt(purchase.total_amount)}
                                </Title>
                            </Descriptions.Item>
                            {purchase.remarks && (
                                <Descriptions.Item label={<Typography.Text strong>Remarks</Typography.Text>}>
                                    {purchase.remarks}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </Col>
            </Row>

            <JournalVoucherModal
                visible={voucherVisible}
                onCancel={() => setVoucherVisible(false)}
                data={purchase}
            />
        </div>
    );
};

export default PurchaseDetailsPage;





